import moment from 'moment'
import { calendarEventsApi } from '~/stores/calendar.store'

export const useCalendarEvent = () => {
  // Helper function to parse and merge custom fields data
  const parseCustomFields = (event: CalendarEvent): CalendarEvent => {
    if (event?.custom_fields_0?.t_1_value) {
      try {
        const parsedData = JSON.parse(event.custom_fields_0?.t_1_value) as Partial<CalendarEvent>

        // Merge the parsed data into the event object
        Object.entries(parsedData).forEach(([key, val]) => {
          if (val !== undefined) {
            ;(event as any)[key] = val
          }
        })

        // Mark as parsed to avoid re-parsing
        event.hasReportParsed = true
      } catch (error) {
        console.error('Failed to parse custom fields:', error)
      }
    }
    return event
  }

  const fetchCalendarEvent = async (
    eventId: number | string | string[]
  ): Promise<CalendarEvent | null> => {
    try {
      const response = await useApi(
        `calendar-events/${eventId}?include=category,calendarEventStatus,project.projectType,contact,user,custom_fields_0,project.custom_fields_0,project.managerRelation,project.contacts.contact,project.mainCustomer,category.user,project.department`,
        'GET'
      )

      if (response?.data) {
        return parseCustomFields(response.data as CalendarEvent)
      }

      return null
    } catch (error) {
      console.error('Failed to fetch calendar event:', error)

      // When offline, the request will fail but Workbox will serve from cache if available
      // The error is expected when offline, so we don't need to show a toast
      if (!navigator.onLine) {
        console.log('Offline mode: Calendar event may be served from cache')
      }

      return null
    }
  }

  const fetchCalendarEventsByDate = async (dateKey: string): Promise<CalendarEvent[]> => {
    try {
      // Parse the date and create a range for the entire day
      const date = moment(dateKey)
      const startOfDay = date.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss')
      const endOfDay = date.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss')

      // Use the existing calendarEventsApi with proper filters
      const response = await calendarEventsApi.fetch(1, 'title', 1, {
        startsBetween: [startOfDay, endOfDay]
      })

      if (response?.response?.data) {
        return (response.response.data as CalendarEvent[]).map((event) => parseCustomFields(event))
      }

      return []
    } catch (error) {
      console.error('Failed to fetch calendar events by date:', error)

      // When offline, return empty array as Workbox will serve from cache if available
      if (!navigator.onLine) {
        console.log('Offline mode: Calendar events may be served from cache')
      }

      return []
    }
  }

  const fetchCalendarEventsByDateRange = async (
    startDate: string,
    endDate: string
  ): Promise<CalendarEvent[]> => {
    try {
      const start = moment(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')
      const end = moment(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')

      const response = await calendarEventsApi.fetch(1, 'title', 1, {
        startsBetween: [start, end]
      })

      if (response?.response?.data) {
        return (response.response.data as CalendarEvent[]).map((event) => parseCustomFields(event))
      }

      return []
    } catch (error) {
      console.error('Failed to fetch calendar events by date range:', error)

      if (!navigator.onLine) {
        console.log('Offline mode: Calendar events may be served from cache')
      }

      return []
    }
  }

  const fetchCalendarEventWithDetails = async (
    eventId: number,
    dateKey: string
  ): Promise<CalendarEvent> => {
    const localEvent = await getEventById(eventId, dateKey)
    if (localEvent) {
      return localEvent
    }

    isCalendarEventLoading.value = true
    const response = await useApi(
      `calendar-events/${eventId}?include=category,calendarEventStatus,project.projectType,contact,user,custom_fields_0,category,calendarEventStatus,project.projectType,contact,user,custom_fields_0,project.custom_fields_0,project.managerRelation,project.contacts.contact,project.mainCustomer,category.user,project.department,project.department.emailSetting`,
      'GET'
    )

    const calendarEvent = parseCustomFields(response.data)

    await downloadAndSetFileUrls(calendarEvent)

    const calendarEvents = (await getEvent(dateKey)) || []
    const index = calendarEvents?.findIndex((event) => event.id === eventId)

    if (index !== -1) calendarEvents[index] = calendarEvent
    else calendarEvents?.push(calendarEvent)

    await setEvent(dateKey, calendarEvents)
    isCalendarEventLoading.value = false
    return calendarEvent
  }

  /**
   * Fetch calendar events with INSTANT LOCAL-FIRST strategy
   * 1. ALWAYS return cached data immediately (instant UI)
   * 2. In background: sync with API and update cache
   * 3. This guarantees instant page loads even with slow/no connection
   */
  const fetchCalendarEventsWithLocalMerge = async (dateKey: string): Promise<CalendarEvent[]> => {
    // 1. ALWAYS load from cache FIRST (instant!)
    const cachedEvents = (await getEvent(dateKey)) || []

    if (cachedEvents.length > 0) {
      console.log('[CalendarEvent] ✅ Using cached data (instant):', cachedEvents.length)
    }

    // 2. If online, sync in BACKGROUND (non-blocking)
    if (navigator.onLine) {
      // Don't await - let it sync in background!
      syncEventsInBackground(dateKey, cachedEvents, setEvent).catch((error) => {
        console.warn('[CalendarEvent] Background sync failed (data still safe):', error)
      })
    } else {
      console.log('[CalendarEvent] Offline - using cached data only')
    }

    // 3. Return cached data immediately (or empty if no cache)
    return cachedEvents
  }

  /**
   * Background sync - updates cache from API without blocking UI
   */
  async function syncEventsInBackground(
    dateKey: string,
    cachedEvents: CalendarEvent[],
    setEvent: any
  ): Promise<void> {
    const { startSync, completeSync, failSync } = useSyncStatus()

    try {
      startSync()
      console.log('[CalendarEvent] 🔄 Background sync started...')

      const date = moment(dateKey)
      const startOfDay = date.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss')
      const endOfDay = date.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss')

      const response = await useApi(
        `calendar-events?filter[startsBetween]=${startOfDay},${endOfDay}&include=category,calendarEventStatus,project.projectType,contact,user,custom_fields_0,project.custom_fields_0,project.managerRelation,project.contacts.contact,project.mainCustomer,category.user,project.department&per_page=${Number.MAX_SAFE_INTEGER}`,
        'GET'
      )

      const apiEvents = (response?.data as CalendarEvent[]) || []

      // Merge API events with cached local data
      const mergedEvents = apiEvents.map((apiEvent) => {
        const parsedApiEvent = parseCustomFields(apiEvent)

        const localEvent = cachedEvents.find((le) => le.id === apiEvent.id)

        if (!localEvent) {
          return parsedApiEvent
        }

        // Merge: API structure + local report data (local wins)
        const localReportData = localEvent.custom_fields_0?.t_1_value
          ? JSON.parse(localEvent.custom_fields_0.t_1_value)
          : null

        const mergedEvent = {
          ...(localReportData
            ? {
                declarationOfAssignment: localReportData.declarationOfAssignment,
                causesOfDamage: localReportData.causesOfDamage,
                recommendedActions: localReportData.recommendedActions,
                exteriorView: localReportData.exteriorView,
                measurementProtocol: localReportData.measurementProtocol,
                additionalServices: localReportData.additionalServices,
                annotations: localReportData.annotations,
                serviceSetup: localReportData.serviceSetup,
                damageReportPdf: localReportData.damageReportPdf,
                damageMitigation: localReportData.damageMitigation,
                damageReport: localReportData.damageReport,
                presentPersons: localReportData.presentPersons,
                services: localReportData.services,
                extendedDamageReports: localReportData.extendedDamageReports,
                files: localReportData.files,
                hasPreviewSeen: localReportData.hasPreviewSeen,
                timeTrackingDialogDismissed: localReportData.timeTrackingDialogDismissed
              }
            : {}),
          ...parsedApiEvent
        }

        return mergedEvent
      })

      // Save updated data back to cache
      await setEvent(dateKey, mergedEvents)

      completeSync()
      console.log('[CalendarEvent] ✅ Background sync complete:', mergedEvents.length)
    } catch (error: any) {
      // Silent fail - cached data is still valid
      failSync(error.message || 'Sync failed')
      console.warn('[CalendarEvent] Background sync failed:', error)
      throw error
    }
  }

  return {
    fetchCalendarEvent,
    fetchCalendarEventsByDate,
    fetchCalendarEventsByDateRange,
    fetchCalendarEventWithDetails,
    fetchCalendarEventsWithLocalMerge
  }
}
